import { Component, computed, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { MonthlyProfitStats } from "@front/m19-api-client";
import { AccountSelectionService, AuthService, StatsService, UserSelectionService } from "@front/m19-services";
import { toSignal } from "@angular/core/rxjs-interop";
import { combineLatest, map, switchMap } from "rxjs";
import { tap } from "rxjs/operators";
import { formatDate, formatNumber, formatPercent } from "@angular/common";
import { IIconComponent, ITooltipDirective } from "@front/m19-ui";
import { ExportToCsv } from "export-to-csv";
import { LoadingService } from "@m19-board/services/loading.service";

export interface LossAndProfitRow {
  label: string;
  values: Array<string>;
  raw_values: Array<number>;
  empty: boolean;
  expand?: boolean;
  rows?: Array<LossAndProfitRow>;
  tooltip?: string;
}

@Component({
  selector: "app-profit-and-loss",
  imports: [ExportButtonComponent, TranslocoDirective, ITooltipDirective, IIconComponent],
  templateUrl: "./profit-and-loss.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfitAndLossComponent {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly statsService = inject(StatsService);
  private readonly authService = inject(AuthService);
  private readonly translocoService = inject(TranslocoService);
  private readonly loadingService = inject(LoadingService);

  private readonly NET_SALES_LABEL = "metrics.ORDER_GLOBAL_NET_SALES_title";
  private readonly ADVERTISING_LABEL = "metrics.ORDER_GLOBAL_ADV_title";
  private readonly FEES_LABEL = "metrics.ORDER_GLOBAL_FEE_title";
  private readonly FBA_GLOBAL_REIMBURSEMENT_LABEL = "metrics.FBA_GLOBAL_REIMBURSEMENT_title";
  protected readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$);
  protected readonly currency = toSignal(this.userSelectionService.selectedCurrency$);
  private readonly locale = toSignal(this.authService.loggedUser$.pipe(map((user) => user.locale)));
  private readonly monthlyProfitStats = toSignal(
    combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      tap(() => this.loadingService.loadingOn()),
      switchMap(([am, currency]) =>
        this.statsService.getMonthlyProfitStats(am.accountId, am.marketplace, currency, am.dspAdvertiserId),
      ),
      tap(() => this.loadingService.loadingOff()),
    ),
    { initialValue: <MonthlyProfitStats[]>[] },
  );

  protected readonly months = computed<string[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => formatDate(stat.date, "MMM yyyy", this.locale()!));
  });

  private readonly netSales = computed<number[]>(() => {
    const grossSales = this.grossSales();
    const promotions = this.promotions();
    const refundedSales = this.refundedSales();
    return grossSales.map((value, i) => value + (promotions[i] ?? 0) + (refundedSales[i] ?? 0));
  });

  private readonly grossSales = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => stat.grossSales ?? 0);
  });

  private readonly promotions = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => stat.promotion ?? 0);
  });

  private readonly refundedSales = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => stat.refundedSales ?? 0);
  });

  private readonly advertising = computed<number[]>(() => {
    const spCost = this.spCost();
    const sbCost = this.sbCost();
    const sdCost = this.sdCost();
    const dspCost = this.dspCost();
    return spCost.map((value, i) => value + sbCost[i] + sdCost[i] + dspCost[i]);
  });

  private readonly spCost = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => (!stat.spCost ? 0 : -stat.spCost!));
  });

  private readonly sbCost = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => (!stat.sbCost ? 0 : -stat.sbCost!));
  });

  private readonly sdCost = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => (!stat.sdCost ? 0 : -stat.sdCost!));
  });

  private readonly dspCost = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => (!stat.dspCost ? 0 : -stat.dspCost!));
  });

  private readonly fees = computed<number[]>(() => {
    const referralFee = this.referralFee();
    const fbaFee = this.fbaFee();
    const fbaStorageFee = this.fbaStorageFee();
    const longTermStorageFee = this.longTermStorageFee();
    const liquidationFee = this.liquidationFee();
    const otherFee = this.otherFee();
    return referralFee.map(
      (value, i) =>
        value +
        (fbaFee[i] ?? 0) +
        (fbaStorageFee[i] ?? 0) +
        (longTermStorageFee[i] ?? 0) +
        (liquidationFee[i] ?? 0) +
        (otherFee[i] ?? 0),
    );
  });

  private readonly referralFee = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => stat.referralFee ?? 0);
  });

  private readonly fbaFee = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => stat.fbaFee ?? 0);
  });

  private readonly fbaStorageFee = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => stat.fbaStorageFee ?? 0);
  });

  private readonly longTermStorageFee = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => stat.longTermStorageFee ?? 0);
  });

  private readonly liquidationFee = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => stat.liquidations ?? 0);
  });

  private readonly internationalFreight = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => stat.internationalFreight ?? 0);
  });

  private readonly otherFee = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => stat.otherFee ?? 0);
  });

  private readonly fbaInventory = computed<number[]>(() => {
    const other = this.fbaOtherReimbursement();
    const reversal = this.fbaReversalReimbursement();
    return other.map((value, i) => value + reversal[i]);
  });

  private readonly fbaOtherReimbursement = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => stat.fbaOtherReimbursement ?? 0);
  });

  private readonly fbaReversalReimbursement = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => stat.fbaReversalReimbursement ?? 0);
  });

  private readonly costOfGoods = computed<number[]>(() => {
    return this.monthlyProfitStats()?.map((stat) => stat.costOfGoods ?? 0);
  });

  private readonly profit = computed<number[]>(() => {
    const netSales = this.netSales();
    const advertising = this.advertising();
    const fees = this.fees();
    const fbaInventory = this.fbaInventory();
    const costOfGoods = this.costOfGoods();
    return netSales.map((value, i) => {
      return value + advertising[i] + fees[i] + fbaInventory[i] + costOfGoods[i];
    });
  });

  private readonly profitMargin = computed<string[]>(() => {
    const profit = this.profit();
    const netSales = this.netSales();
    return profit.map((value, i) => {
      if (netSales[i] === 0) {
        return "-";
      }
      if (value < 0) {
        return "(" + formatPercent(-value / netSales[i], this.locale()!, "1.1-1") + ")";
      }
      return formatPercent(value / netSales[i], this.locale()!, "1.1-1");
    });
  });

  private readonly expandNetSales = signal<boolean>(false);
  private readonly expandAdvertising = signal<boolean>(false);
  private readonly expandFees = signal<boolean>(false);
  private readonly expandFbaInventory = signal<boolean>(false);

  protected rows = computed<Array<LossAndProfitRow>>(() => {
    return [
      {
        label: this.NET_SALES_LABEL,
        values: this.format(this.netSales()),
        raw_values: this.netSales(),
        expand: this.expandNetSales(),
        empty: this.checkEmpty(this.netSales()),
        rows: [
          {
            label: "metrics.ORDER_SALES_title",
            values: this.format(this.grossSales()),
            raw_values: this.grossSales(),
            empty: this.checkEmpty(this.grossSales()),
            tooltip: this.translocoService.translate("profit-and-loss.sales_are_aggregated_at_the_purchased_date"),
          },
          {
            label: "metrics.PROMOTION_title",
            values: this.format(this.promotions()),
            raw_values: this.promotions(),
            empty: this.checkEmpty(this.promotions()),
          },
          {
            label: "metrics.REFUNDED_SALES_title",
            values: this.format(this.refundedSales()),
            raw_values: this.refundedSales(),
            empty: this.checkEmpty(this.refundedSales()),
            tooltip: this.translocoService.translate("profit-and-loss.chargeback_tooltip"),
          },
        ],
        tooltip: this.translocoService.translate("profit-and-loss.net_sales"),
      },
      {
        label: this.ADVERTISING_LABEL,
        values: this.format(this.advertising()),
        raw_values: this.advertising(),
        empty: this.checkEmpty(this.advertising()),
        expand: this.expandAdvertising(),
        rows: [
          {
            label: "metrics.SP_ADVERTISING_title",
            values: this.format(this.spCost()),
            raw_values: this.spCost(),
            empty: this.checkEmpty(this.spCost()),
          },
          {
            label: "metrics.SB_ADVERTISING_title",
            values: this.format(this.sbCost()),
            raw_values: this.sbCost(),
            empty: this.checkEmpty(this.sbCost()),
          },
          {
            label: "metrics.SD_ADVERTISING_title",
            values: this.format(this.sdCost()),
            raw_values: this.sdCost(),
            empty: this.checkEmpty(this.sdCost()),
          },
          {
            label: "metrics.DSP_ADVERTISING_title",
            values: this.format(this.dspCost()),
            raw_values: this.dspCost(),
            empty: this.checkEmpty(this.dspCost()),
          },
        ],
      },
      {
        label: this.FEES_LABEL,
        values: this.format(this.fees()),
        raw_values: this.fees(),
        empty: this.checkEmpty(this.fees()),
        expand: this.expandFees(),
        rows: [
          {
            label: "metrics.REFERRAL_FEE_title",
            values: this.format(this.referralFee()),
            raw_values: this.referralFee(),
            empty: this.checkEmpty(this.referralFee()),
          },
          {
            label: "metrics.FBA_FEE_title",
            values: this.format(this.fbaFee()),
            raw_values: this.fbaFee(),
            empty: this.checkEmpty(this.fbaFee()),
            tooltip: this.translocoService.translate("profit-and-loss.fba_fulfillment_fee"),
          },
          {
            label: "metrics.FBA_STORAGE_FEE_title",
            values: this.format(this.fbaStorageFee()),
            raw_values: this.fbaStorageFee(),
            empty: this.checkEmpty(this.fbaStorageFee()),
          },
          {
            label: "metrics.FBA_LONG_TERM_STORAGE_FEE_tooltip",
            values: this.format(this.longTermStorageFee()),
            raw_values: this.longTermStorageFee(),
            empty: this.checkEmpty(this.longTermStorageFee()),
          },
          {
            label: "metrics.LIQUIDATIONS_FEE_title",
            values: this.format(this.liquidationFee()),
            raw_values: this.liquidationFee(),
            empty: this.checkEmpty(this.liquidationFee()),
          },
          {
            label: "metrics.INTERNATIONAL_FREIGHT_FEE_title",
            values: this.format(this.internationalFreight()),
            raw_values: this.internationalFreight(),
            empty: this.checkEmpty(this.internationalFreight()),
          },
          {
            label: "metrics.OTHER_FEE_title",
            values: this.format(this.otherFee()),
            raw_values: this.otherFee(),
            empty: this.checkEmpty(this.otherFee()),
            tooltip: this.translocoService.translate("profit-and-loss.other_fee"),
          },
        ],
      },
      {
        label: this.FBA_GLOBAL_REIMBURSEMENT_LABEL,
        values: this.format(this.fbaInventory()),
        raw_values: this.fbaInventory(),
        empty: this.checkEmpty(this.fbaInventory()),
        expand: this.expandFbaInventory(),
        rows: [
          {
            label: "metrics.FBA_REVERSAL_REIMBURSEMENT_title",
            values: this.format(this.fbaReversalReimbursement()),
            raw_values: this.fbaReversalReimbursement(),
            empty: this.checkEmpty(this.fbaReversalReimbursement()),
          },
          {
            label: "metrics.FBA_OTHER_REIMBURSEMENT_title",
            values: this.format(this.fbaOtherReimbursement()),
            raw_values: this.fbaOtherReimbursement(),
            empty: this.checkEmpty(this.fbaOtherReimbursement()),
          },
        ],
        tooltip: this.translocoService.translate("profit-and-loss.fba_tooltip"),
      },
      {
        label: "metrics.COST_OF_GOODS_title",
        values: this.format(this.costOfGoods()),
        raw_values: this.costOfGoods(),
        empty: this.checkEmpty(this.costOfGoods()),
      },
      {
        label: "metrics.PROFIT_WITH_GLOBAL_FEES_title",
        values: this.format(this.profit()),
        raw_values: this.profit(),
        empty: this.checkEmpty(this.profit()),
      },
      {
        label: "metrics.ORDER_GLOBAL_MARGIN_title",
        values: this.profitMargin(),
        raw_values: this.profit().map((value, i) => (value / this.netSales()[i]) * 100),
        empty: false,
      },
    ];
  });

  protected hasNoData = computed<boolean>(() => {
    return this.rows().every((row) => row.values.length === 0);
  });

  private format(a: Array<number>): Array<string> {
    return a.map((value) => {
      return value < 0
        ? "(" + formatNumber(value * -1, this.locale()!, "1.0-0") + ")"
        : formatNumber(value, this.locale()!, "1.0-0");
    });
  }

  private checkEmpty(data: Array<number>): boolean {
    return data.every((number) => number === 0);
  }

  protected downloadFile(): void {
    const months = this.months();
    const currency = this.currency()!;
    const rows = this.rows();

    const header = ["metric", ...months, "currency"];
    const allStats: Record<string, string>[] = [];

    rows.forEach((row) => {
      let stat: Record<string, string> = {};
      stat[""] = this.translocoService.translate(row.label);
      months.forEach((month, i) => {
        stat[month] = row.raw_values[i].toFixed(0);
      });
      stat["currency"] = currency;
      allStats.push(stat);
      for (const subRaw of row.rows || []) {
        if (!subRaw.empty) {
          stat = {};
          stat[""] = this.translocoService.translate(subRaw.label);
          months.forEach((month, i) => {
            stat[month] = subRaw.raw_values[i].toFixed(2);
          });
          stat["currency"] = currency;
          allStats.push(stat);
        }
      }
    });

    const options = {
      fieldSeparator: ",",
      quoteStrings: '"',
      decimalSeparator: ".",
      showLabels: true,
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
      filename:
        "profit_and_loss_" +
        (this.accountMarketplace()!.accountGroupName ?? "") +
        "_" +
        this.accountMarketplace()!.marketplace,
      headers: header,
    };

    const csvExporter = new ExportToCsv(options);

    csvExporter.generateCsv(allStats);
  }

  protected expandRow(label: string) {
    switch (label) {
      case this.NET_SALES_LABEL:
        this.expandNetSales.update((v) => !v);
        break;
      case this.ADVERTISING_LABEL:
        this.expandAdvertising.update((v) => !v);
        break;
      case this.FEES_LABEL:
        this.expandFees.update((v) => !v);
        break;
      case this.FBA_GLOBAL_REIMBURSEMENT_LABEL:
        this.expandFbaInventory.update((v) => !v);
        break;
    }
  }
}
